import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { isGroup, OPERATORS_BY_TYPE, type Condition, type FormField, type Operator, type Rule, type RuleGroup } from '@re/form-engine';
import type { Lookups } from '@re/form-ui';
import { Autocomplete, Box, IconButton, ListSubheader, MenuItem, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { arabicFilter } from '../../lib/search';
import { Button } from '../../components/button';
import { TextField } from '../../components/text-field';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import type { LocationRow } from '../../api/types';
import { OPERATOR_LABEL } from '../../lib/labels';
import { isDisplay } from './helpers';
import { isEn, nameOf, tr } from '../../lib/i18n';

const MULTI_VALUE_OPS: Operator[] = ['in', 'not_in', 'contains_any', 'contains_all'];
const NO_VALUE_OPS: Operator[] = ['is_empty', 'is_not_empty'];

interface Ctx {
  /** fields that come BEFORE the edited field (conditions may only reference those) */
  candidates: FormField[];
  lookups?: Lookups;
}

/** Top-level editor: null = always; otherwise an `all`/`any` group. */
export function RuleEditor({ value, onChange, candidates, lookups, emptyLabel }: {
  value: Rule | null | undefined;
  onChange: (r: Rule | null) => void;
  candidates: FormField[];
  lookups?: Lookups;
  emptyLabel: string;
  labelOf: (key: string) => string;
}) {
  const usable = candidates.filter((f) => !isDisplay(f.type));
  const root: RuleGroup | null = !value ? null : isGroup(value) ? value : { all: [value] };

  if (!root) {
    return (
      <Stack spacing={1} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">{emptyLabel}</Typography>
        <Button size="small" startIcon={<AddIcon />} disabled={!usable.length} onClick={() => onChange({ all: [defaultCondition(usable[0]!)] })}>
          {tr('إضافة شرط', 'Add condition')}
        </Button>
        {!usable.length && <Typography variant="caption" color="text.secondary">{tr('لا توجد حقول قبل هذا الحقل يمكن الاعتماد عليها.', 'There are no earlier fields this one can depend on.')}</Typography>}
      </Stack>
    );
  }
  return (
    <Stack spacing={1}>
      <GroupEditor group={root} onChange={(g) => onChange(g && ((g.all?.length ?? 0) + (g.any?.length ?? 0) > 0 || g.not) ? g : null)} ctx={{ candidates: usable, lookups }} depth={0} />
    </Stack>
  );
}

function defaultCondition(f: FormField): Condition {
  const op = OPERATORS_BY_TYPE[f.type][0] ?? 'is_not_empty';
  return { field: f.key, op, value: NO_VALUE_OPS.includes(op) ? undefined : MULTI_VALUE_OPS.includes(op) ? [] : '' };
}

function GroupEditor({ group, onChange, ctx, depth }: { group: RuleGroup; onChange: (g: RuleGroup | null) => void; ctx: Ctx; depth: number }) {
  const mode: 'all' | 'any' = group.any && !group.all ? 'any' : 'all';
  const items: Rule[] = group[mode] ?? [];
  const setItems = (next: Rule[]) => onChange(next.length ? { [mode]: next } : null);
  return (
    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: depth ? 'action.hover' : undefined }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, m) => m && onChange({ [m]: items })}>
          <ToggleButton value="all">{tr('كل الشروط (و)', 'All conditions (AND)')}</ToggleButton>
          <ToggleButton value="any">{tr('أي شرط (أو)', 'Any condition (OR)')}</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        {depth > 0 && <IconButton size="small" onClick={() => onChange(null)} aria-label={tr('حذف المجموعة', 'Delete group')}><DeleteOutlineIcon fontSize="small" /></IconButton>}
      </Stack>
      <Stack spacing={1}>
        {items.map((it, i) =>
          isGroup(it) ? (
            <GroupEditor key={i} group={it} depth={depth + 1} ctx={ctx} onChange={(g) => setItems(g ? items.map((x, j) => (j === i ? g : x)) : items.filter((_, j) => j !== i))} />
          ) : (
            <ConditionRow key={i} cond={it} ctx={ctx} onChange={(c) => setItems(c ? items.map((x, j) => (j === i ? c : x)) : items.filter((_, j) => j !== i))} />
          ),
        )}
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setItems([...items, defaultCondition(ctx.candidates[0]!)])}>{tr('شرط', 'Condition')}</Button>
        {depth < 2 && <Button size="small" startIcon={<AddIcon />} onClick={() => setItems([...items, { any: [defaultCondition(ctx.candidates[0]!)] }])}>{tr('مجموعة', 'Group')}</Button>}
      </Stack>
    </Paper>
  );
}

function ConditionRow({ cond, ctx, onChange }: { cond: Condition; ctx: Ctx; onChange: (c: Condition | null) => void }) {
  const field = ctx.candidates.find((f) => f.key === cond.field);
  const ops = field ? OPERATORS_BY_TYPE[field.type] : [];
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-start' }}>
      <Autocomplete
        size="small"
        options={ctx.candidates}
        value={field ?? null}
        disableClearable={!!field}
        getOptionLabel={(f) => f.label.ar || f.key}
        filterOptions={arabicFilter((f: FormField) => [f.label.ar, f.label.en, f.key])}
        isOptionEqualToValue={(a, b) => a.key === b.key}
        onChange={(_, f) => f && onChange(defaultCondition(f))}
        sx={{ minWidth: 200, flex: 1 }}
        renderInput={(p) => <TextField {...p} label={tr('الحقل', 'Field')} error={!field} helperText={!field ? tr(`حقل غير متاح: ${cond.field}`, `Field not available: ${cond.field}`) : undefined} />}
      />
      <TextField size="small" select label={tr('العامل', 'Operator')} value={ops.includes(cond.op) ? cond.op : ''} sx={{ minWidth: 150 }}
        onChange={(e) => {
          const op = e.target.value as Operator;
          const v = NO_VALUE_OPS.includes(op) ? undefined : MULTI_VALUE_OPS.includes(op) ? (Array.isArray(cond.value) ? cond.value : cond.value ? [cond.value] : []) : op === 'between' ? ['', ''] : Array.isArray(cond.value) ? (cond.value[0] ?? '') : cond.value;
          onChange({ ...cond, op, value: v });
        }}>
        {ops.map((o) => <MenuItem key={o} value={o}>{OPERATOR_LABEL[o]}</MenuItem>)}
      </TextField>
      <Box sx={{ flex: 2, minWidth: 200 }}>{field && !NO_VALUE_OPS.includes(cond.op) && <ValueInput field={field} cond={cond} lookups={ctx.lookups} onChange={(value) => onChange({ ...cond, value })} />}</Box>
      <IconButton onClick={() => onChange(null)} aria-label={tr('حذف الشرط', 'Delete condition')}><DeleteOutlineIcon /></IconButton>
    </Stack>
  );
}

type CodeOpt = { value: string; label: string; en?: string | null; group?: string };
function codeOptions(field: FormField, lookups?: Lookups): CodeOpt[] | null {
  switch (field.type) {
    case 'request_type':
      return lookups?.requestTypes.map((r) => ({ value: r.code, label: nameOf(r), en: r.name_en })) ?? [];
    case 'property_category':
      return lookups?.categories.map((r) => ({ value: r.code, label: nameOf(r), en: r.name_en })) ?? [];
    case 'property_type':
      if (!lookups) return [];
      // grouped by category, in category order
      return lookups.categories.flatMap((c): CodeOpt[] => lookups.propertyTypes.filter((t) => t.category_id === c.id).map((r) => ({ value: r.code, label: nameOf(r), en: r.name_en, group: nameOf(c) })))
        .concat(lookups.propertyTypes.filter((t) => !lookups.categories.some((c) => c.id === t.category_id)).map((r): CodeOpt => ({ value: r.code, label: nameOf(r), en: r.name_en })));
    case 'property_features':
      return lookups?.features.map((r) => ({ value: r.code, label: nameOf(r), en: r.name_en })) ?? [];
    case 'radio':
    case 'dropdown':
    case 'checkbox_group':
      return (field.config.options ?? []).map((o) => ({ value: o.value, label: isEn() && o.label.en ? o.label.en : o.label.ar, en: o.label.en }));
    default:
      return null;
  }
}

function ValueInput({ field, cond, lookups, onChange }: { field: FormField; cond: Condition; lookups?: Lookups; onChange: (v: unknown) => void }) {
  const opts = codeOptions(field, lookups);
  const multi = MULTI_VALUE_OPS.includes(cond.op);

  if (opts) {
    const filter = arabicFilter((o: CodeOpt) => [o.label, o.en, o.value, o.group]);
    if (multi) {
      const vals = (Array.isArray(cond.value) ? cond.value : []) as string[];
      const grouped = opts.some((o) => o.group);
      return (
        <Autocomplete multiple size="small" options={opts} disableCloseOnSelect limitTags={6} filterOptions={filter}
          groupBy={grouped ? (o) => o.group ?? '' : undefined}
          getOptionLabel={(o) => o.label} isOptionEqualToValue={(a, b) => a.value === b.value}
          value={opts.filter((o) => vals.includes(o.value))}
          onChange={(_, v) => onChange(v.map((x) => x.value))}
          renderGroup={grouped ? (p) => {
            const codes = opts.filter((o) => (o.group ?? '') === p.group).map((o) => o.value);
            const allOn = codes.every((c) => vals.includes(c));
            return (
              <li key={p.key}>
                <ListSubheader sx={{ display: 'flex', alignItems: 'center', lineHeight: '36px', top: -8 }}>
                  <Box sx={{ flex: 1 }}>{p.group}</Box>
                  <Button size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => onChange(allOn ? vals.filter((v) => !codes.includes(v)) : Array.from(new Set([...vals, ...codes])))}>
                    {allOn ? tr('إلغاء الكل', 'Clear all') : tr('الكل', 'All')}
                  </Button>
                </ListSubheader>
                <ul style={{ padding: 0 }}>{p.children}</ul>
              </li>
            );
          } : undefined}
          renderInput={(p) => <TextField {...p} label={tr(`القيم (${vals.length})`, `Values (${vals.length})`)} error={!vals.length} />} />
      );
    }
    return (
      <Autocomplete size="small" options={opts} filterOptions={filter} groupBy={opts.some((o) => o.group) ? (o) => o.group ?? '' : undefined}
        getOptionLabel={(o) => o.label} isOptionEqualToValue={(a, b) => a.value === b.value}
        value={opts.find((o) => o.value === cond.value) ?? null} onChange={(_, o) => onChange(o?.value ?? '')}
        renderInput={(p) => <TextField {...p} label={tr('القيمة', 'Value')} error={!cond.value} />} />
    );
  }
  if (field.type === 'boolean') {
    return (
      <TextField size="small" select fullWidth label={tr('القيمة', 'Value')} value={String(cond.value ?? '')} onChange={(e) => onChange(e.target.value === 'true')}>
        <MenuItem value="true">{tr('نعم', 'Yes')}</MenuItem>
        <MenuItem value="false">{tr('لا', 'No')}</MenuItem>
      </TextField>
    );
  }
  if (field.type === 'location') return <GovernoratePicker value={cond.value} onChange={onChange} />;
  const inputType = field.type === 'date' ? 'date' : ['number', 'currency'].includes(field.type) ? 'number' : 'text';
  const conv = (s: string) => (inputType === 'number' ? (s === '' ? '' : Number(s)) : s);
  if (cond.op === 'between') {
    const [a, b] = (Array.isArray(cond.value) ? cond.value : ['', '']) as unknown[];
    return (
      <Stack direction="row" spacing={1}>
        <TextField size="small" type={inputType} label={tr('من', 'From')} value={a ?? ''} onChange={(e) => onChange([conv(e.target.value), b])} slotProps={{ inputLabel: { shrink: true } }} />
        <TextField size="small" type={inputType} label={tr('إلى', 'To')} value={b ?? ''} onChange={(e) => onChange([a, conv(e.target.value)])} slotProps={{ inputLabel: { shrink: true } }} />
      </Stack>
    );
  }
  if (multi) {
    return <TextField size="small" fullWidth label={tr('القيم (مفصولة بفاصلة)', 'Values (comma-separated)')} value={(Array.isArray(cond.value) ? cond.value : []).join(',')} onChange={(e) => onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />;
  }
  return <TextField size="small" fullWidth type={inputType} label={tr('القيمة', 'Value')} value={(cond.value as string) ?? ''} onChange={(e) => onChange(conv(e.target.value))} slotProps={{ inputLabel: { shrink: true } }} />;
}

/** location_in → { governorateId } (first active country's governorates) */
function GovernoratePicker({ value, onChange }: { value: unknown; onChange: (v: unknown) => void }) {
  const countries = useQuery({ queryKey: ['loc', 'countries', undefined], queryFn: () => api<{ items: LocationRow[] }>('/countries') });
  const cid = countries.data?.items[0]?.id;
  const govs = useQuery({ queryKey: ['loc', 'governorates', cid], queryFn: () => api<{ items: LocationRow[] }>('/governorates', { query: { parentId: cid } }), enabled: !!cid });
  const current = (value as { governorateId?: number } | undefined)?.governorateId ?? '';
  return (
    <TextField size="small" select fullWidth label={tr('المحافظة', 'Governorate')} value={current} onChange={(e) => onChange({ governorateId: Number(e.target.value) })}>
      {(govs.data?.items ?? []).map((g) => <MenuItem key={g.id} value={g.id}>{nameOf(g)}</MenuItem>)}
    </TextField>
  );
}
